import { useEffect, useRef, useSyncExternalStore } from 'react';
import {
  BeatQueue,
  beatKindForSource,
  splitStaccato,
  toastDurationMs,
} from '@aeonia/gaia-client';
import type { Beat, ExperienceSession, SessionLine, VoiceState } from '@aeonia/gaia-client';

/**
 * Feeds every new session line into a BeatQueue (beats.ts) and exposes
 * whatever it is currently showing: one beat at a time, in arrival order,
 * cleared per the toast rule unless it is the step's live question. See
 * R-DISP-03/04 and R-ONB-02 in docs/glasses-client/06-gdd-high-level-requirements.md.
 *
 * During speech, VoiceTurn's actual current line owns the text and clears
 * older timed beats. It appears when synthesis starts and holds through
 * playback. Without spoken output, opening lines retain their timed
 * staccato presentation and passing lines retain normal reading durations.
 *
 * `active` gates when a beat's hold actually starts counting down: while the
 * sting plays, the card area is not on screen at all (GlassesApp holds it
 * behind `cardsReady`), so a line that arrived mid-sting is held back and
 * only handed to the queue once `active` turns true. Otherwise its hold
 * would run out unseen and the player would land on an empty card.
 */
export default function useBeatQueue(session: ExperienceSession, voice: VoiceState, active: boolean): Beat | null {
  const queueRef = useRef<BeatQueue | null>(null);
  if (queueRef.current === null) queueRef.current = new BeatQueue();
  const queue = queueRef.current;

  const lastLineRef = useRef<SessionLine | null>(null);
  const speakingRef = useRef(false);
  const pendingRef = useRef<SessionLine[]>([]);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => () => queue.dispose(), [queue]);

  const present = (line: SessionLine): void => {
    const pieces = splitStaccato(line.text, line.source);
    const kind = beatKindForSource(line.source);
    const waitForPlayback = pieces.length === 1 && speakingRef.current;
    pieces.forEach((text) => {
      queue.enqueue(text, kind, waitForPlayback ? null : toastDurationMs(text), line);
    });
  };

  // Enqueue whenever the session's line changes to a new object, once the
  // card is actually on screen to show it; before that, lines queue up
  // in arrival order and are presented the moment it becomes active.
  useEffect(() => {
    return session.subscribe((state) => {
      const line = state.line;
      if (!line || line === lastLineRef.current) return;
      lastLineRef.current = line;
      if (activeRef.current) {
        present(line);
      } else {
        pendingRef.current.push(line);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, queue]);

  useEffect(() => {
    if (!active || pendingRef.current.length === 0) return;
    const pending = pendingRef.current;
    pendingRef.current = [];
    pending.forEach(present);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Track voice playback so a currently-speaking single-piece beat can wait
  // for the real end of speech instead of the toast timer, and so a beat
  // whose hold is already waiting on playback gets released when it ends.
  useEffect(() => {
    const wasSpeaking = speakingRef.current;
    const isSpeaking = voice.phase === 'speaking';
    speakingRef.current = isSpeaking;
    if (active && (isSpeaking || voice.phase === 'failed') && voice.speakingLine) {
      // The audio queue supplies its actual current line, not the latest
      // server line (which may still be waiting for earlier audio).
      queue.showSpoken(voice.speakingLine.text, beatKindForSource(voice.speakingLine.source), voice.speakingLine, voice.phase === 'failed' ? toastDurationMs(voice.speakingLine.text) : null);
    }
    if (wasSpeaking && !isSpeaking && voice.phase !== 'failed') queue.endHold();
  }, [voice.phase, voice.speakingLine, active, queue]);

  const subscribe = (onChange: () => void) => queue.subscribe(onChange);
  return useSyncExternalStore(subscribe, () => queue.current, () => queue.current);
}
